import React, { useMemo, useState } from 'react';
import ProductPhotoUpload from './ProductPhotoUpload';
import './custom-jewelry.css';
import {
  getJewelryFieldsForProduct,
  getJewelryProfile,
  getProductJewelryProfileId,
} from './jewelryCustomization';

/**
 * Renders jewelry customization fields for a product (locket photo, chain, ring size, etc.).
 */
export default function JewelryCustomizer({
  product,
  values,
  onChange,
  disabled = false,
  idPrefix = 'jewelry',
  showIntro = true,
}) {
  const profileId = getProductJewelryProfileId(product);
  const profile = profileId ? getJewelryProfile(profileId) : null;
  const fields = useMemo(() => getJewelryFieldsForProduct(product), [product]);

  if (!profile || fields.length === 0) return null;

  const setField = (fieldId, val) => {
    onChange?.({ ...values, [fieldId]: val });
  };

  return (
    <div className="jewelry-customizer" onClick={(e) => e.stopPropagation()}>
      {showIntro ? (
        <div className="jewelry-customizer__intro">
          <span className="jewelry-customizer__badge">Customize</span>
          <p className="jewelry-customizer__profile-name mb-1">{profile.label}</p>
          {profile.hint ? <p className="jewelry-customizer__hint small text-muted mb-0">{profile.hint}</p> : null}
        </div>
      ) : null}

      <div className="jewelry-customizer__fields">
        {fields.map((field) => {
          const fieldId = `${idPrefix}-${field.id}`;

          if (field.type === 'photo') {
            return (
              <div key={field.id} className="jewelry-customizer__field">
                <ProductPhotoUpload
                  id={fieldId}
                  label={field.label}
                  hint={field.hint}
                  required={field.required}
                  value={values[field.id] || ''}
                  onChange={(url) => setField(field.id, url)}
                  disabled={disabled}
                />
              </div>
            );
          }

          if (field.type === 'select') {
            return (
              <div key={field.id} className="jewelry-customizer__field">
                <label className="jewelry-customizer__label" htmlFor={fieldId}>
                  {field.label}
                  {field.required ? <span className="jewelry-customizer__req"> *</span> : null}
                </label>
                <select
                  id={fieldId}
                  className="form-select form-select-sm jewelry-customizer__select"
                  value={values[field.id] || ''}
                  disabled={disabled}
                  onChange={(e) => setField(field.id, e.target.value)}
                >
                  <option value="">Select…</option>
                  {(field.options || []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                      {opt.priceDelta > 0 ? ` (+₹${opt.priceDelta})` : opt.priceDelta < 0 ? ` (−₹${Math.abs(opt.priceDelta)})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            );
          }

          return (
            <div key={field.id} className="jewelry-customizer__field">
              <label className="jewelry-customizer__label" htmlFor={fieldId}>
                {field.label}
                {field.required ? <span className="jewelry-customizer__req"> *</span> : null}
              </label>
              <input
                id={fieldId}
                type="text"
                className="form-control form-control-sm"
                value={values[field.id] || ''}
                maxLength={field.maxLength || 200}
                disabled={disabled}
                onChange={(e) => setField(field.id, e.target.value)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function useJewelryCustomizerState() {
  return useState({});
}
